import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HANDLES,
  NUDGE_STEP,
  FINE_NUDGE_STEP,
  applyImageAlign,
  applyImageWidth,
  clampPercent,
  getContainingBlockWidth,
  intersects,
  measureBox,
  measureViewport,
  minPercentFor,
  normalizeInlineWidth,
  pxToPercent,
  readEffectiveWidthPercent,
  readImageAlign,
  sameBox,
  snapPercent,
} from './imageSizing';

/** Holding an arrow key must not re-render the whole drawer on every repeat. */
const EMIT_DEBOUNCE_MS = 150;

/**
 * Selecting and resizing an image inside a contentEditable editor.
 *
 * Pointer Events with `setPointerCapture` (not mouse events on `document`) so the
 * drag works with a mouse, touch and pen alike, and cannot be stranded when the
 * pointer leaves the editor mid-gesture.
 *
 * The selected image is held as a raw DOM node, so everything hinges on that node
 * surviving `emitChange`. It does, because RichTextEditor skips the value→innerHTML
 * sync for the HTML it just emitted; `remeasure` clears the selection defensively
 * if the node is ever replaced anyway.
 */
const useImageResize = ({ editorRef, containerRef, emitChange, enabled }) => {
  const [selectedImg, setSelectedImg] = useState(null);
  const [box, setBox] = useState(null);
  const [viewport, setViewport] = useState(null);
  const [widthPercent, setWidthPercentState] = useState(null);
  const [align, setAlignState] = useState(null);
  const [dragPercent, setDragPercent] = useState(null);

  // Mirror of `selectedImg` for event handlers, which must not be re-created (and
  // their observers torn down) every time the parent re-renders.
  const selectedImgRef = useRef(null);
  const dragRef = useRef(null);
  const emitTimer = useRef(null);

  // `onChange` is an inline arrow in the parent, so `emitChange` has a new identity
  // on every render. Reading it through a ref keeps every callback below stable.
  const emitChangeRef = useRef(emitChange);
  emitChangeRef.current = emitChange;

  const scheduleEmit = useCallback(() => {
    if (emitTimer.current) clearTimeout(emitTimer.current);
    emitTimer.current = setTimeout(() => {
      emitTimer.current = null;
      emitChangeRef.current?.();
    }, EMIT_DEBOUNCE_MS);
  }, []);

  const emitNow = useCallback(() => {
    if (emitTimer.current) {
      clearTimeout(emitTimer.current);
      emitTimer.current = null;
    }
    emitChangeRef.current?.();
  }, []);

  const flushPendingEmit = useCallback(() => {
    if (!emitTimer.current) return;
    clearTimeout(emitTimer.current);
    emitTimer.current = null;
    emitChangeRef.current?.();
  }, []);

  const clearSelection = useCallback(() => {
    flushPendingEmit();
    selectedImgRef.current = null;
    dragRef.current = null;
    setSelectedImg(null);
    setBox(null);
    setDragPercent(null);
    setWidthPercentState(null);
    setAlignState(null);
  }, [flushPendingEmit]);

  const containingWidth = useCallback(
    (img) => getContainingBlockWidth(img, editorRef.current),
    [editorRef],
  );

  const syncFromImage = useCallback((img) => {
    const cbWidth = containingWidth(img);
    setWidthPercentState(readEffectiveWidthPercent(img, cbWidth));
    setAlignState(readImageAlign(img));
  }, [containingWidth]);

  const remeasure = useCallback(() => {
    const img = selectedImgRef.current;
    const container = containerRef.current;
    if (!img || !container) return;

    const nextBox = measureBox(img, container);
    if (!nextBox) {
      // The node was replaced or removed under us — drop the selection rather
      // than leave an outline anchored to a detached element.
      clearSelection();
      return;
    }

    setBox((prev) => (sameBox(prev, nextBox) ? prev : nextBox));

    const nextViewport = measureViewport(editorRef.current, container);
    setViewport((prev) => (sameBox(prev, nextViewport) ? prev : nextViewport));
  }, [containerRef, editorRef, clearSelection]);

  const selectImage = useCallback((img) => {
    if (!img) return;
    selectedImgRef.current = img;
    setSelectedImg(img);
    syncFromImage(img);
    remeasure();
  }, [syncFromImage, remeasure]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  // Each one normalises a legacy px width first, so an image becomes percent the
  // first time it is actually touched — never merely by opening the guide.

  const setWidthPercent = useCallback((pct) => {
    const img = selectedImgRef.current;
    if (!img) return;

    const cbWidth = containingWidth(img);
    normalizeInlineWidth(img, cbWidth);
    applyImageWidth(img, pct == null ? null : clampPercent(pct, minPercentFor(cbWidth)));
    syncFromImage(img);
    emitNow();
    requestAnimationFrame(remeasure);
  }, [containingWidth, syncFromImage, emitNow, remeasure]);

  const resetWidth = useCallback(() => setWidthPercent(null), [setWidthPercent]);

  /**
   * `immediate` emits at once (a button click, which may be followed straight
   * away by Save); the keyboard leaves it false so a held arrow key does not
   * re-render the whole drawer on every repeat.
   */
  const nudgeWidth = useCallback((delta, immediate = false) => {
    const img = selectedImgRef.current;
    if (!img) return;

    const cbWidth = containingWidth(img);
    normalizeInlineWidth(img, cbWidth);
    // An auto-sized image has no percent to add to, so start from what it
    // actually renders at — the first nudge is then a step, not a jump.
    const current = readEffectiveWidthPercent(img, cbWidth)
      ?? clampPercent(pxToPercent(img.getBoundingClientRect().width, cbWidth))
      ?? 100;

    applyImageWidth(img, clampPercent(current + delta, minPercentFor(cbWidth)));
    syncFromImage(img);
    if (immediate) emitNow(); else scheduleEmit();
    requestAnimationFrame(remeasure);
  }, [containingWidth, syncFromImage, scheduleEmit, emitNow, remeasure]);

  const alignImage = useCallback((mode) => {
    const img = selectedImgRef.current;
    if (!img) return;

    normalizeInlineWidth(img, containingWidth(img));
    // Clicking the active alignment turns it off again.
    applyImageAlign(img, readImageAlign(img) === mode ? null : mode);
    syncFromImage(img);
    emitNow();
    requestAnimationFrame(remeasure);
  }, [containingWidth, syncFromImage, emitNow, remeasure]);

  const deleteImage = useCallback(() => {
    const img = selectedImgRef.current;
    if (!img) return;
    img.remove();
    emitNow(); // before clearing, so a pending nudge cannot emit the old width after
    clearSelection();
  }, [clearSelection, emitNow]);

  // ── Drag ──────────────────────────────────────────────────────────────────

  const onHandlePointerDown = useCallback((event, handleKey) => {
    if (event.button != null && event.button !== 0) return;
    const img = selectedImgRef.current;
    if (!img) return;

    // Stops contentEditable starting a text selection, and stops the editor's
    // own onClick from clearing the selection we are about to resize.
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    const cbWidth = containingWidth(img);
    normalizeInlineWidth(img, cbWidth);

    const startPx = img.getBoundingClientRect().width;
    const minPct = minPercentFor(cbWidth);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startPx,
      dirX: HANDLES.find((h) => h.key === handleKey)?.dirX ?? 1,
      cbWidth,
      minPct,
    };
    setDragPercent(clampPercent(pxToPercent(startPx, cbWidth), minPct));
  }, [containingWidth]);

  const onHandlePointerMove = useCallback((event) => {
    const drag = dragRef.current;
    const img = selectedImgRef.current;
    if (!drag || !img || event.pointerId !== drag.pointerId) return;

    const nextPx = drag.startPx + (event.clientX - drag.startX) * drag.dirX;
    let pct = clampPercent(pxToPercent(nextPx, drag.cbWidth), drag.minPct);
    // A pointer event without usable coordinates must do nothing — falling
    // through with null would clear the width and snap the image to natural size
    // mid-drag.
    if (pct == null) return;
    if (event.shiftKey) pct = clampPercent(snapPercent(pct), drag.minPct);

    applyImageWidth(img, pct);
    setDragPercent(pct);
    setWidthPercentState(pct);
    // Measured synchronously: the style write forced layout anyway, and the
    // outline has to track the image within the same frame or it visibly lags.
    remeasure();
  }, [remeasure]);

  const endDrag = useCallback((event) => {
    if (!dragRef.current) return;
    event?.currentTarget?.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
    setDragPercent(null);

    const img = selectedImgRef.current;
    if (img) syncFromImage(img);
    emitNow(); // once, on release — not sixty times a second during the drag
  }, [syncFromImage, emitNow]);

  const getHandleProps = useCallback((handleKey) => ({
    onPointerDown: (event) => onHandlePointerDown(event, handleKey),
    onPointerMove: onHandlePointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  }), [onHandlePointerDown, onHandlePointerMove, endDrag]);

  // ── Wiring ────────────────────────────────────────────────────────────────

  const editorHandlers = useMemo(() => ({
    onClick: (event) => {
      if (!enabled) return;
      if (event.target?.tagName === 'IMG') selectImage(event.target);
      else if (selectedImgRef.current) clearSelection();
    },
    onScroll: () => {
      if (selectedImgRef.current) remeasure();
    },
  }), [enabled, selectImage, clearSelection, remeasure]);

  /** Read-only mode and the HTML source view have no image selection. */
  useEffect(() => {
    if (!enabled) clearSelection();
  }, [enabled, clearSelection]);

  useEffect(() => () => {
    if (emitTimer.current) clearTimeout(emitTimer.current);
  }, []);

  useEffect(() => {
    if (!enabled || !selectedImg) return undefined;

    const onKeyDown = (event) => {
      // The percent field lives inside the overlay — let it keep its own keys.
      if (event.target?.closest?.('input, textarea, select')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        clearSelection();
        editorRef.current?.focus();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteImage();
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        // The caret is still inside the contentEditable, so without this the
        // arrow keys would move it instead of resizing.
        event.preventDefault();
        const step = event.shiftKey ? FINE_NUDGE_STEP : NUDGE_STEP;
        nudgeWidth(event.key === 'ArrowRight' ? step : -step);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, selectedImg, clearSelection, deleteImage, nudgeWidth, editorRef]);

  /** Clicking anywhere outside the editor drops the selection. */
  useEffect(() => {
    if (!enabled || !selectedImg) return undefined;

    const onDocumentMouseDown = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      clearSelection();
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [enabled, selectedImg, clearSelection, containerRef]);

  /** Keep the overlay glued to the image through every kind of reflow. */
  useEffect(() => {
    if (!enabled || !selectedImg) return undefined;

    const onWindowResize = () => remeasure();
    window.addEventListener('resize', onWindowResize);

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => remeasure())
      : null;
    if (observer) {
      observer.observe(selectedImg);
      if (editorRef.current) observer.observe(editorRef.current);
    }

    // A just-pasted base64 image, or one still fetching from S3, measures as a
    // zero-width sliver — percent maths against that is meaningless.
    const onLoad = () => remeasure();
    if (!selectedImg.complete) selectedImg.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('resize', onWindowResize);
      observer?.disconnect();
      selectedImg.removeEventListener('load', onLoad);
    };
  }, [enabled, selectedImg, remeasure, editorRef]);

  return {
    selectedImg,
    box,
    viewport,
    widthPercent,
    align,
    dragPercent,
    isDragging: dragPercent != null,
    isVisible: intersects(box, viewport),
    selectImage,
    clearSelection,
    remeasure,
    setWidthPercent,
    resetWidth,
    nudgeWidth,
    alignImage,
    deleteImage,
    getHandleProps,
    editorHandlers,
  };
};

export default useImageResize;
